/**
 * MOCK DATA — KPI cards and analytics chart series derived from the synthetic
 * ledger.
 *
 * See `src/mock/banner.ts`. The arithmetic here follows §3.3 deliberately:
 * net P&L is gross − commission − swap − fees, win rate is wins ÷ (wins +
 * losses) with breakeven trades excluded from the denominator and reported
 * separately. Getting this wrong in the mock layer would teach the real
 * implementation the wrong definitions.
 */
import type {
  AnalyticsChartId,
  ChartPoint,
  ChartSeries,
  ClosedTradeRow,
  KpiCard,
  KpiId,
  KpiSnapshot,
  TradeOutcome,
} from '../api/types';
import { KPI_HELP, KPI_LABELS, KPI_ORDER } from '../lib/labels';
import { formatDuration, formatInteger, formatPercent, formatPrice } from '../lib/format';
import type { MockDataset } from './dataset';

const DEMO_EQUITY_BASE = 10_000;
const DAILY_LOSS_CAP_PCT = 2.0;

function sum(values: readonly number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

function mean(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  return sum(values) / values.length;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

interface OutcomeBuckets {
  wins: ClosedTradeRow[];
  losses: ClosedTradeRow[];
  breakeven: ClosedTradeRow[];
}

function bucketByOutcome(trades: readonly ClosedTradeRow[]): OutcomeBuckets {
  const buckets: OutcomeBuckets = { wins: [], losses: [], breakeven: [] };
  for (const trade of trades) {
    const key: TradeOutcome = trade.outcome;
    if (key === 'win') buckets.wins.push(trade);
    else if (key === 'loss') buckets.losses.push(trade);
    else buckets.breakeven.push(trade);
  }
  return buckets;
}

/** §3.2 A — one card per listed metric, in spec order, with honest null handling. */
export function buildKpiSnapshot(dataset: MockDataset): KpiSnapshot {
  const trades = dataset.closed_trades;
  const { wins, losses, breakeven } = bucketByOutcome(trades);

  const nets = trades.map((trade) => trade.net_realized_pnl);
  const realized = round2(sum(nets));
  const unrealized = round2(sum(dataset.positions.map((position) => position.unrealized_pnl)));

  const grossProfit = round2(sum(wins.map((trade) => trade.net_realized_pnl)));
  const grossLoss = round2(sum(losses.map((trade) => trade.net_realized_pnl)));

  const winRate = wins.length + losses.length === 0 ? null : wins.length / (wins.length + losses.length);
  const profitFactor = grossLoss === 0 ? null : Math.abs(grossProfit / grossLoss);
  const expectancy = mean(nets);
  const averageWin = mean(wins.map((trade) => trade.net_realized_pnl));
  const averageLoss = mean(losses.map((trade) => trade.net_realized_pnl));
  const largestWin = wins.length === 0 ? null : Math.max(...wins.map((trade) => trade.net_realized_pnl));
  const largestLoss = losses.length === 0 ? null : Math.min(...losses.map((trade) => trade.net_realized_pnl));

  // Equity walk to get the intraday high-water mark and the current drawdown.
  let equity = DEMO_EQUITY_BASE;
  let highWater = DEMO_EQUITY_BASE;
  let maxDrawdown = 0;
  for (const trade of [...trades].sort((left, right) => left.exit_timestamp.localeCompare(right.exit_timestamp))) {
    equity += trade.net_realized_pnl;
    highWater = Math.max(highWater, equity);
    maxDrawdown = Math.max(maxDrawdown, highWater - equity);
  }
  const currentDrawdown = round2(highWater - equity);

  const dailyLossCap = round2((DEMO_EQUITY_BASE * DAILY_LOSS_CAP_PCT) / 100);
  const lossToDate = Math.max(0, -(realized + unrealized));
  const dailyLossRemaining = round2(dailyLossCap - lossToDate);

  const results = Object.values(dataset.decision_artifacts).flatMap((artifacts) => artifacts.results);
  const fillLatency = mean(results.map((result) => result.fill_latency_ms).filter((value): value is number => value !== null));
  const estimatedSlippage = mean(
    results.map((result) => result.estimated_slippage_points).filter((value): value is number => value !== null),
  );
  const actualSlippage = mean(
    results.map((result) => result.actual_slippage_points).filter((value): value is number => value !== null),
  );

  const ordersSubmitted = results.length + dataset.working_orders.length;
  const ordersRejected = dataset.signals.filter((candidate) => candidate.status === 'rejected').length;

  const cards: KpiCard[] = KPI_ORDER.map((id) => buildCard(id, {
    trades,
    wins,
    losses,
    breakeven,
    realized,
    unrealized,
    grossProfit,
    grossLoss,
    winRate,
    profitFactor,
    expectancy,
    averageWin,
    averageLoss,
    largestWin,
    largestLoss,
    currentDrawdown,
    dailyLossCap,
    dailyLossRemaining,
    ordersSubmitted,
    ordersRejected,
    fillLatency,
    estimatedSlippage,
    actualSlippage,
    notional: round2(sum(dataset.positions.map((position) => position.notional))),
    dataset,
  }));

  return {
    as_of: new Date(dataset.now).toISOString(),
    session_date: dataset.session_date,
    account_currency: dataset.account_currency,
    breakeven_threshold: dataset.breakeven_threshold,
    cards,
  };
}

interface CardInputs {
  trades: readonly ClosedTradeRow[];
  wins: readonly ClosedTradeRow[];
  losses: readonly ClosedTradeRow[];
  breakeven: readonly ClosedTradeRow[];
  realized: number;
  unrealized: number;
  grossProfit: number;
  grossLoss: number;
  winRate: number | null;
  profitFactor: number | null;
  expectancy: number | null;
  averageWin: number | null;
  averageLoss: number | null;
  largestWin: number | null;
  largestLoss: number | null;
  currentDrawdown: number;
  dailyLossCap: number;
  dailyLossRemaining: number;
  ordersSubmitted: number;
  ordersRejected: number;
  fillLatency: number | null;
  estimatedSlippage: number | null;
  actualSlippage: number | null;
  notional: number;
  dataset: MockDataset;
}

function card(
  id: KpiId,
  value: number | null,
  display: string,
  format: KpiCard['format'],
  tone: KpiCard['tone'],
  overrides: Partial<KpiCard> = {},
): KpiCard {
  return {
    id,
    label: KPI_LABELS[id],
    value,
    display,
    format,
    tone,
    sublabel: null,
    lower_is_better: false,
    availability: 'ok',
    help: KPI_HELP[id],
    unavailable_reason: null,
    ...overrides,
  };
}

function cardUnavailable(id: KpiId, reason: string): KpiCard {
  return {
    id,
    label: KPI_LABELS[id],
    value: null,
    display: '—',
    format: 'decimal',
    tone: 'muted',
    sublabel: null,
    lower_is_better: false,
    availability: 'unavailable',
    help: KPI_HELP[id],
    unavailable_reason: reason,
  };
}

function buildCard(id: KpiId, inputs: CardInputs): KpiCard {
  const currency = inputs.dataset.account_currency;
  const money = (value: number | null, signed = false): string =>
    value === null ? '—' : new Intl.NumberFormat(undefined, { style: 'currency', currency, signDisplay: signed ? 'always' : 'auto' }).format(value);

  switch (id) {
    case 'closed_trades_today':
      return card(id, inputs.trades.length, formatInteger(inputs.trades.length), 'integer', 'neutral', {
        sublabel: 'completed round trips',
      });
    case 'open_positions':
      return card(id, inputs.dataset.positions.length, `${inputs.dataset.positions.length}`, 'count_pair', 'neutral', {
        sublabel: `${money(inputs.notional)} notional`,
      });
    case 'win_rate':
      return card(id, inputs.winRate, formatPercent(inputs.winRate), 'percent', inputs.winRate !== null && inputs.winRate >= 0.5 ? 'positive' : 'negative', {
        sublabel: `wins ÷ (wins + losses), breakeven excluded · threshold ${money(inputs.dataset.breakeven_threshold, false)}`,
      });
    case 'wins_losses_breakeven':
      return card(id, inputs.wins.length, `${inputs.wins.length} / ${inputs.losses.length} / ${inputs.breakeven.length}`, 'count_pair', 'neutral', {
        sublabel: 'after commission, swap and fees',
      });
    case 'realized_pnl':
      return card(id, inputs.realized, money(inputs.realized, true), 'currency', inputs.realized >= 0 ? 'positive' : 'negative');
    case 'unrealized_pnl':
      return card(id, inputs.unrealized, money(inputs.unrealized, true), 'currency', inputs.unrealized >= 0 ? 'positive' : 'negative');
    case 'net_pnl_today':
      return card(id, round2(inputs.realized + inputs.unrealized), money(round2(inputs.realized + inputs.unrealized), true), 'currency', inputs.realized + inputs.unrealized >= 0 ? 'positive' : 'negative', {
        sublabel: 'realized + unrealized − costs',
      });
    case 'gross_profit':
      return card(id, inputs.grossProfit, money(inputs.grossProfit), 'currency', 'positive');
    case 'gross_loss':
      return card(id, inputs.grossLoss, money(inputs.grossLoss), 'currency', 'negative');
    case 'profit_factor':
      return card(id, inputs.profitFactor, inputs.profitFactor === null ? '—' : `${inputs.profitFactor.toFixed(2)}×`, 'ratio', inputs.profitFactor !== null && inputs.profitFactor >= 1 ? 'positive' : 'negative');
    case 'expectancy_per_trade':
      return card(id, inputs.expectancy, money(inputs.expectancy, true), 'currency', inputs.expectancy !== null && inputs.expectancy >= 0 ? 'positive' : 'negative');
    case 'average_win':
      return card(id, inputs.averageWin, money(inputs.averageWin, true), 'currency', 'positive');
    case 'average_loss':
      return card(id, inputs.averageLoss, money(inputs.averageLoss, true), 'currency', 'negative');
    case 'largest_win':
      return card(id, inputs.largestWin, money(inputs.largestWin, true), 'currency', 'positive');
    case 'largest_loss':
      return card(id, inputs.largestLoss, money(inputs.largestLoss, true), 'currency', 'negative');
    case 'current_drawdown':
      return card(id, inputs.currentDrawdown, money(inputs.currentDrawdown), 'currency', inputs.currentDrawdown > 0 ? 'warning' : 'ok', {
        lower_is_better: true,
        sublabel: 'from intraday high-water equity',
      });
    case 'daily_loss_remaining':
      return card(
        id,
        inputs.dailyLossRemaining,
        money(inputs.dailyLossRemaining),
        'currency',
        inputs.dailyLossRemaining <= 0 ? 'critical' : inputs.dailyLossRemaining < inputs.dailyLossCap * 0.35 ? 'warning' : 'ok',
        {
          lower_is_better: true,
          sublabel: `cap ${money(inputs.dailyLossCap)} at ${DAILY_LOSS_CAP_PCT.toFixed(1)}% of ${money(DEMO_EQUITY_BASE)} demo equity`,
        },
      );
    case 'orders_submitted':
      return card(id, inputs.ordersSubmitted, formatInteger(inputs.ordersSubmitted), 'integer', 'neutral', {
        sublabel: 'intents handed to the EA',
      });
    case 'orders_rejected':
      return card(id, inputs.ordersRejected, formatInteger(inputs.ordersRejected), 'integer', inputs.ordersRejected > 0 ? 'warning' : 'ok', {
        lower_is_better: true,
        sublabel: 'rejected by risk, the EA or the broker',
      });
    case 'average_fill_latency':
      return card(id, inputs.fillLatency, formatDuration(inputs.fillLatency), 'duration_ms', 'neutral', {
        lower_is_better: true,
        sublabel: 'intent time to confirmed fill',
      });
    case 'slippage':
      return card(
        id,
        inputs.actualSlippage,
        inputs.actualSlippage === null ? '—' : `${formatPrice(inputs.actualSlippage, 1)} pts vs ${formatPrice(inputs.estimatedSlippage, 1)} est`,
        'pips',
        inputs.actualSlippage !== null && inputs.estimatedSlippage !== null && inputs.actualSlippage > inputs.estimatedSlippage ? 'warning' : 'ok',
        {
          lower_is_better: true,
          sublabel: 'benchmarked against the decision quote',
        },
      );
    default: {
      // Exhaustiveness guard: adding a KpiId without a card is a compile error.
      const exhaustive: never = id;
      return cardUnavailable(exhaustive, 'This metric has no mock derivation yet.');
    }
  }
}

/* ------------------------------------------------------------------ *
 * Analytics charts — §3.2 B
 * ------------------------------------------------------------------ */

const HOLDING_BUCKETS: readonly { label: string; maxMs: number }[] = [
  { label: '< 15 m', maxMs: 15 * 60_000 },
  { label: '15–30 m', maxMs: 30 * 60_000 },
  { label: '30–60 m', maxMs: 60 * 60_000 },
  { label: '1–2 h', maxMs: 2 * 60 * 60_000 },
  { label: '2–4 h', maxMs: 4 * 60 * 60_000 },
  { label: '> 4 h', maxMs: Number.POSITIVE_INFINITY },
];

function groupBy(trades: readonly ClosedTradeRow[], key: (trade: ClosedTradeRow) => string): Map<string, ClosedTradeRow[]> {
  const groups = new Map<string, ClosedTradeRow[]>();
  for (const trade of trades) {
    const groupKey = key(trade);
    const existing = groups.get(groupKey);
    if (existing === undefined) groups.set(groupKey, [trade]);
    else existing.push(trade);
  }
  return groups;
}

function pnlBars(groups: Map<string, ClosedTradeRow[]>, sortNumeric: boolean): ChartPoint[] {
  const points = [...groups.entries()].map(([key, rows]) => ({
    bucket: key,
    net_pnl: round2(sum(rows.map((row) => row.net_realized_pnl))),
    trades: rows.length,
    win_rate: rows.length === 0 ? null : round2((rows.filter((row) => row.outcome === 'win').length / rows.length) * 100),
  }));
  if (sortNumeric) return points.sort((left, right) => Number(left.bucket) - Number(right.bucket));
  return points.sort((left, right) => left.bucket.localeCompare(right.bucket));
}

export function buildChart(dataset: MockDataset, chartId: AnalyticsChartId): ChartSeries {
  const trades = [...dataset.closed_trades].sort((left, right) => left.exit_timestamp.localeCompare(right.exit_timestamp));
  const asOf = new Date(dataset.now).toISOString();

  const base = (title: string, unit: string, series: ChartSeries['series'], points: ChartPoint[], subtitle: string | null = null, notes: string[] = []): ChartSeries => ({
    chart_id: chartId,
    title,
    subtitle,
    unit,
    series,
    points,
    as_of: asOf,
    sample_size: trades.length,
    notes,
  });

  switch (chartId) {
    case 'cumulative_realized_pnl': {
      let cumulative = 0;
      const points = trades.map((trade, index) => {
        cumulative = round2(cumulative + trade.net_realized_pnl);
        return { label: `#${index + 1}`, at: trade.exit_timestamp, cumulative, trade_pnl: trade.net_realized_pnl };
      });
      return base('Intraday cumulative realized P&L', 'account currency', [{ key: 'cumulative', label: 'Cumulative net P&L', color_token: 'series-1' }], points);
    }
    case 'equity_curve': {
      let realizedEquity = DEMO_EQUITY_BASE;
      const points = trades.map((trade, index) => {
        realizedEquity = round2(realizedEquity + trade.net_realized_pnl);
        // "Marked" equity adds a deterministic mark-to-market wobble for the
        // most recent trade only; the daemon must send the real account marks.
        const markedEquity = round2(realizedEquity + (index === trades.length - 1 ? sum(dataset.positions.map((p) => p.unrealized_pnl)) : 0));
        return { label: `#${index + 1}`, at: trade.exit_timestamp, realized_equity: realizedEquity, marked_equity: markedEquity };
      });
      return base(
        'Equity curve',
        'account currency',
        [
          { key: 'realized_equity', label: 'Realized equity', color_token: 'series-1' },
          { key: 'marked_equity', label: 'Marked equity', color_token: 'series-2' },
        ],
        points,
        null,
        ['Mock marked equity only includes the currently open positions at the final point; the daemon must supply a real mark-to-market series.'],
      );
    }
    case 'win_loss_distribution': {
      const buckets = bucketByOutcome(trades);
      return base('Win / loss distribution', 'trades', [{ key: 'count', label: 'Trades', color_token: 'series-1' }], [
        { bucket: 'Wins', count: buckets.wins.length },
        { bucket: 'Losses', count: buckets.losses.length },
        { bucket: 'Breakeven', count: buckets.breakeven.length },
      ]);
    }
    case 'pnl_by_hour': {
      const groups = groupBy(trades, (trade) => String(new Date(trade.exit_timestamp).getUTCHours()).padStart(2, '0'));
      return base('P&L by hour of day', 'account currency', [{ key: 'net_pnl', label: 'Net P&L', color_token: 'series-1' }], pnlBars(groups, true), 'Exit hour, UTC');
    }
    case 'pnl_by_weekday': {
      const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const groups = groupBy(trades, (trade) => names[new Date(trade.exit_timestamp).getUTCDay()] ?? 'Unknown');
      return base('P&L by day of week', 'account currency', [{ key: 'net_pnl', label: 'Net P&L', color_token: 'series-1' }], pnlBars(groups, false));
    }
    case 'pnl_by_instrument':
      return base('P&L by instrument', 'account currency', [{ key: 'net_pnl', label: 'Net P&L', color_token: 'series-1' }], pnlBars(groupBy(trades, (trade) => trade.symbol), false));
    case 'pnl_by_strategy':
      return base('P&L by strategy version', 'account currency', [{ key: 'net_pnl', label: 'Net P&L', color_token: 'series-1' }], pnlBars(groupBy(trades, (trade) => trade.strategy_version), false));
    case 'pnl_by_regime':
      return base('P&L by market regime', 'account currency', [{ key: 'net_pnl', label: 'Net P&L', color_token: 'series-1' }], pnlBars(groupBy(trades, (trade) => `${trade.regime?.trend_strength ?? 'unknown'} / ${trade.regime?.volatility_bucket ?? 'unknown'}`), false));
    case 'slippage_distribution': {
      const values = Object.values(dataset.decision_artifacts)
        .flatMap((artifacts) => artifacts.results)
        .map((result) => result.actual_slippage_points)
        .filter((value): value is number => value !== null);
      const edges = [-4, -2, 0, 2, 4, 6];
      const points = edges.map((edge, index) => {
        const next = edges[index + 1];
        const count = values.filter((value) => (next === undefined ? value >= edge : value >= edge && value < next)).length;
        return { bucket: next === undefined ? `≥ ${edge}` : `${edge} to ${next}`, count };
      });
      return base('Actual slippage distribution', 'trades', [{ key: 'count', label: 'Trades', color_token: 'series-1' }], points, 'Points of adverse (positive) or favourable (negative) slippage versus the decision quote');
    }
    case 'holding_time_distribution': {
      const points = HOLDING_BUCKETS.map((bucket, index) => {
        const upper = bucket.maxMs;
        const lower = index === 0 ? 0 : (HOLDING_BUCKETS[index - 1]?.maxMs ?? 0);
        return { bucket: bucket.label, count: trades.filter((trade) => trade.holding_time_ms >= lower && trade.holding_time_ms < upper).length };
      });
      return base('Holding-time distribution', 'trades', [{ key: 'count', label: 'Trades', color_token: 'series-1' }], points);
    }
    case 'consecutive_streaks': {
      const points: ChartPoint[] = [];
      let current = 0;
      let wins = 0;
      let losses = 0;
      let maxWin = 0;
      let maxLoss = 0;
      trades.forEach((trade, index) => {
        const delta = trade.outcome === 'win' ? 1 : trade.outcome === 'loss' ? -1 : 0;
        if (delta === 0) return;
        if (current === 0 || Math.sign(current) === delta) current += delta;
        else current = delta;
        maxWin = Math.max(maxWin, current);
        maxLoss = Math.min(maxLoss, current);
        if (current > 0) wins += 1;
        else losses += 1;
        points.push({ label: `#${index + 1}`, at: trade.exit_timestamp, streak: current });
      });
      return base(
        'Consecutive wins / losses',
        'streak length',
        [
          { key: 'streak', label: 'Signed streak', color_token: 'series-1' },
        ],
        points,
        `Longest winning streak ${maxWin}, longest losing streak ${Math.abs(maxLoss)}`,
        [`Winning-streak observations ${wins}, losing-streak observations ${losses}.`],
      );
    }
    case 'session_trade_count_winrate': {
      const groups = groupBy(trades, (trade) => trade.regime?.session_phase ?? 'unknown');
      const points = [...groups.entries()].map(([session, rows]) => {
        const decided = rows.filter((row) => row.outcome !== 'breakeven');
        return {
          bucket: session,
          trades: rows.length,
          win_rate: decided.length === 0 ? null : round2((decided.filter((row) => row.outcome === 'win').length / decided.length) * 100),
        };
      });
      return base(
        'Trade count and win rate by session',
        'trades / percent',
        [
          { key: 'trades', label: 'Trades', color_token: 'series-1' },
          { key: 'win_rate', label: 'Win rate %', color_token: 'series-2' },
        ],
        points,
      );
    }
    default: {
      const exhaustive: never = chartId;
      throw new Error(`No mock chart builder for "${String(exhaustive)}"`);
    }
  }
}

export const ALL_CHART_IDS: readonly AnalyticsChartId[] = [
  'cumulative_realized_pnl',
  'equity_curve',
  'win_loss_distribution',
  'pnl_by_hour',
  'pnl_by_weekday',
  'pnl_by_instrument',
  'pnl_by_strategy',
  'pnl_by_regime',
  'slippage_distribution',
  'holding_time_distribution',
  'consecutive_streaks',
  'session_trade_count_winrate',
];

export { DEMO_EQUITY_BASE, DAILY_LOSS_CAP_PCT };
